"use client";

import Link from "next/link";
import CodeMirror from "@uiw/react-codemirror";
// import { xml } from "@codemirror/lang-xml";
import { githubDark } from "@uiw/codemirror-theme-github";
import TrySeozastCTA from "@/app/components/TrySeozastCTA";
import { javascript } from "@codemirror/lang-javascript";
import Head from "next/head";

const Page = () => {
  const canonicalUrl = 'https://seozast.com/articles/how-to-render-markdown-files-dynamically-in-nextjs-for-seo';

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Head con canonical */}
      <Head>
        <title>AI SEO in 2025: How to Make Your Content AI-Citation Ready</title>
        <link rel="canonical" href={canonicalUrl} />
      </Head>
      <section className="mb-8">
        <h1 className="text-3xl font-bold mb-4">How to Render Markdown Files Dynamically in Next.js for SEO</h1>
        <p className="text-md mb-4">
          Rendering Markdown (MD) files dynamically in Next.js is a powerful way to create SEO-friendly content, such as blog posts or documentation. By processing MD files at runtime or build time, you can automatically generate pages, metadata, and tables of contents (TOCs) without hardcoding routes. This guide shows how to implement dynamic Markdown rendering, compares it to static rendering, and explains the folder structure and code used. For related SEO techniques, check our guides on <Link href="/articles/how-to-create-a-dynamic-sitemap-in-nextjs" className="text-emerald-500 hover:underline">dynamic sitemaps</Link>, <Link href="/articles/what-is-pseo-and-seo" className="text-emerald-500 hover:underline">PSEO and SEO basics</Link>, <Link href="/articles/seozast-seo-automation-easy-affordable" className="text-emerald-500 hover:underline">SEOZast automation</Link>, and <Link href="/articles/advantages-seo-automation-seozast" className="text-emerald-500 hover:underline">SEO automation advantages</Link>.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">Why Dynamic Markdown Rendering?</h2>
        <p className="text-md mb-4">
          Dynamic Markdown rendering allows you to store content in MD files and generate pages automatically, making it ideal for blogs, documentation, or content-heavy sites. It improves SEO by enabling dynamic metadata (title, description, tags), automatic TOC generation, and scalable content management. Search engines like Google can crawl these pages efficiently, boosting discoverability. Unlike static rendering, dynamic rendering adapts to new content without requiring code changes, a key advantage for Programmatic SEO (PSEO) as discussed in our <Link href="/articles/what-is-pseo-and-seo" className="text-emerald-500 hover:underline">PSEO and SEO basics guide</Link>.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">Project Folder Structure</h2>
        <p className="text-md mb-4">
          Here’s the folder structure for a Next.js project rendering Markdown files dynamically:
        </p>
        <ul className="list-disc pl-6 text-md mb-4">
          <li><code>app/articles/[slug]/page.tsx</code>: Dynamic route for individual article pages, rendering MD content as HTML.</li>
          <li><code>app/articles/page.tsx</code>: Index page listing all articles with links to their pages.</li>
          <li><code>app/lib/articles.ts</code>: Utility functions to read MD files, extract metadata, and return content.</li>
          <li><code>app/remark-callouts.ts</code>: Custom remark plugin for enhanced Markdown features (e.g., callouts).</li>
          <li><code>articles/</code>: Directory storing MD files, each representing an article.</li>
          <li><code>public/</code>: Static assets like images or CSS files.</li>
          <li><code>styles/</code>: Global styles, often using Tailwind CSS for consistent design.</li>
        </ul>
        <p className="text-md mb-4">
          The <code>articles/</code> directory separates content from code, making it easy to add new articles by dropping MD files. The <code>lib/articles.ts</code> file centralizes logic for reading MD files, while <code>[slug]/page.tsx</code> handles dynamic rendering. The index page (<code>articles/page.tsx</code>) provides a user-friendly overview of all articles.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">Dynamic Markdown Rendering Code</h2>
        <p className="text-md mb-4">
          Below is the code to dynamically render Markdown files in Next.js. It includes utility functions, a dynamic article page, and an index page. Place these files in the respective directories as shown above.
        </p>

        <h3 className="text-xl font-semibold mb-2">Utility Functions (<code>app/lib/articles.ts</code>)</h3>
        <CodeMirror
          value={`import fs from 'fs';
import path from 'path';
import matter from 'gray-matter';

const articlesDir = path.join(process.cwd(), 'articles');

export function getArticleSlugs() {
  return fs.readdirSync(articlesDir).map((file) => file.replace(/\.md$/, ''));
}

export function getArticleBySlug(slug: string) {
  const fullPath = path.join(articlesDir, \`\${slug}.md\`);
  const fileContents = fs.readFileSync(fullPath, 'utf8');
  const { data, content } = matter(fileContents);

  return {
    slug,
    metadata: data,
    content,
  };
}`}
          extensions={[javascript()]}
          theme={githubDark}
          height="300px"
          className="mb-4"
        />
        <p className="text-md mb-4">
          This file reads MD files from the <code>articles/</code> directory, extracts metadata (e.g., title, description) using <code>gray-matter</code>, and returns content for rendering.
        </p>

        <h3 className="text-xl font-semibold mb-2">Dynamic Article Page (<code>app/articles/[slug]/page.tsx</code>)</h3>
        <CodeMirror
          value={`import { getArticleBySlug, getArticleSlugs } from "@/lib/articles";
import { notFound } from "next/navigation";
import { remark } from "remark";
import remarkGfm from "remark-gfm";
import remarkRehype from "remark-rehype";
import rehypeStringify from "rehype-stringify";
import rehypeHighlight from "rehype-highlight";
import rehypeSanitize from "rehype-sanitize";
import rehypeSlug from "rehype-slug";
import remarkCallouts from "@/lib/remark-callouts";
import { Root, Heading } from "mdast";
import "highlight.js/styles/github-dark.css";

export async function generateStaticParams() {
  const slugs = getArticleSlugs();
  return slugs.map((slug) => ({ slug }));
}

interface TocItem {
  id: string;
  title: string;
  level: number;
}

async function generateToc(content: string): Promise<TocItem[]> {
  const processor = remark().use(remarkGfm).use(remarkCallouts);
  const tree = (await processor.parse(content)) as Root;
  const toc: TocItem[] = [];

  tree.children.forEach((node) => {
    if (node.type === "heading" && node.depth >= 2) {
      const heading = node as Heading;
      const title = heading.children
        .filter((child) => child.type === "text")
        .map((child) => child.value)
        .join("");
      const id = title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");
      toc.push({
        id,
        title,
        level: heading.depth,
      });
    }
  });

  return toc;
}

export default async function ArticlePage({ params }: { params: { slug: string } }) {
  const article = getArticleBySlug(params.slug);
  if (!article) return notFound();

  let contentHtml = "";
  try {
    const processedContent = await remark()
      .use(remarkGfm)
      .use(remarkCallouts)
      .use(remarkRehype, { allowDangerousHtml: true })
      .use(rehypeSlug)
      .use(rehypeHighlight)
      .use(rehypeSanitize)
      .use(rehypeStringify)
      .process(article.content || "");

    contentHtml = processedContent.toString();
  } catch (error) {
    console.error("Error processing Markdown:", error);
    contentHtml = "<p>Error rendering content. Please try again later.</p>";
  }

  const toc = await generateToc(article.content || "");

  return (
    <article className="prose dark:prose-dark max-w-3xl mx-auto p-6">
      <header className="mb-8">
        <h1 className="text-4xl font-bold text-foreground mb-4">
          {article.metadata.title || article.slug}
        </h1>
        {article.metadata.description && (
          <p className="text-lg text-muted-foreground">
            {article.metadata.description}
          </p>
        )}
        <div className="mt-4 flex items-center space-x-4 text-sm text-muted-foreground">
          {article.metadata.date && (
            <span>
              {new Date(article.metadata.date).toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </span>
          )}
          {article.metadata.tags && (
            <div className="flex space-x-2">
              {article.metadata.tags.map((tag: string) => (
                <span
                  key={tag}
                  className="px-2 py-1 bg-muted text-muted-foreground rounded-full text-xs"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </header>

      {toc.length > 0 && (
        <nav className="mb-8 p-4 bg-card rounded-lg border border-border bg-gray-800">
          <h2 className="text-xl font-semibold text-foreground mb-4">Table of Contents</h2>
          <ul className="space-y-2 text-white">
            {toc.map((item) => (
              <li
                key={item.id}
                className={\`ml-\${(item.level - 2) * 4} text-foreground hover:text-emerald-500 transition-colors\`}
              >
                <a className="text-white" href={\`#\${item.id}\`}>
                  {item.title.replace('✔', '')}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      )}

      <div
        className="prose dark:prose-dark"
        dangerouslySetInnerHTML={{ __html: contentHtml }}
      />
    </article>
  );
}`}
          extensions={[javascript()]}
          theme={githubDark}
          height="600px"
          className="mb-4"
        />
        <p className="text-md mb-4">
          This page renders an MD file as HTML using <code>remark</code> and <code>rehype</code> plugins, generates a TOC, and displays metadata like title, description, and tags. The <code>generateStaticParams</code> function ensures all articles are pre-rendered for SEO.
        </p>

        <h3 className="text-xl font-semibold mb-2">Articles Index Page (<code>app/articles/page.tsx</code>)</h3>
        <CodeMirror
          value={`import Link from "next/link";
import { getArticleSlugs, getArticleBySlug } from "@/lib/articles";

export default async function ArticlesIndexPage() {
  const slugs = getArticleSlugs();
  const articles = slugs.map((slug) => getArticleBySlug(slug));

  return (
    <div className="max-w-4xl mx-auto py-12 px-4 bg-black text-white">
      <h1 className="text-4xl font-bold mb-8 text-foreground">
        Articles & Guides
      </h1>
      <div className="grid gap-6 md:grid-cols-2">
        {articles.map((article) => (
          <Link
            key={article.slug}
            href={\`/articles/\${article.slug}\`}
            className="group block"
          >
            <div className="bg-gray-900 p-6 bg-card rounded-lg border border-border text-white hover:border-emerald-500 hover:shadow-lg transition-all duration-200">
              <h2 className="text-2xl font-semibold text-foreground group-hover:text-emerald-500">
                {article.metadata.title || article.slug}
              </h2>
              {article.metadata.description && (
                <p className="mt-2 text-muted-foreground line-clamp-2">
                  {article.metadata.description}
                </p>
              )}
              <div className="mt-4 flex items-center space-x-4 text-sm text-muted-foreground">
                {article.metadata.date && (
                  <span>
                    {new Date(article.metadata.date).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </span>
                )}
                {article.metadata.tags && (
                  <div className="flex space-x-2">
                    {article.metadata.tags.map((tag: string) => (
                      <span
                        key={tag}
                        className="px-2 py-1 bg-muted text-muted-foreground rounded-full text-xs bg-emerald-500 text-black"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}`}
          extensions={[javascript()]}
          theme={githubDark}
          height="400px"
          className="mb-4"
        />
        <p className="text-md mb-4">
          This index page lists all articles, displaying their titles, descriptions, dates, and tags, with links to individual article pages.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">Static Markdown Rendering Example</h2>
        <p className="text-md mb-4">
          For comparison, here’s a static approach where article pages are hardcoded in <code>app/articles/[slug]/page.tsx</code>. This requires creating a page for each article manually.
        </p>
        <CodeMirror
          value={`import { notFound } from "next/navigation";

export default async function ArticlePage({ params }: { params: { slug: string } }) {
  const articles = {
    "sample-article": {
      title: "Sample Article",
      description: "A hardcoded article example.",
      date: "2025-01-01",
      content: "<h1>Sample Article</h1><p>This is a hardcoded article rendered as HTML.</p>"
    }
  };

  const article = articles[params.slug];
  if (!article) return notFound();

  return (
    <article className="prose dark:prose-dark max-w-3xl mx-auto p-6">
      <header className="mb-8">
        <h1 className="text-4xl font-bold text-foreground mb-4">{article.title}</h1>
        <p className="text-lg text-muted-foreground">{article.description}</p>
        <div className="mt-4 text-sm text-muted-foreground">
          <span>{new Date(article.date).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })}</span>
        </div>
      </header>
      <div
        className="prose dark:prose-dark"
        dangerouslySetInnerHTML={{ __html: article.content }}
      />
    </article>
  );
}`}
          extensions={[javascript()]}
          theme={githubDark}
          height="400px"
          className="mb-4"
        />
        <p className="text-md mb-4">
          This static approach hardcodes article data within the page, requiring manual updates for each new article. It’s simple for small sites but unscalable for dynamic content.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">Dynamic vs. Static Rendering</h2>
        <p className="text-md mb-4">
          <strong>Dynamic Rendering</strong>: Automatically generates pages from MD files, ideal for blogs or documentation with frequent updates. It scales effortlessly, supports dynamic metadata for SEO, and reduces maintenance by eliminating manual page creation. The provided dynamic code scans the <code>articles/</code> directory, processes MD files, and generates TOCs, making it perfect for content-heavy sites.
        </p>
        <p className="text-md mb-4">
          <strong>Static Rendering</strong>: Relies on hardcoded content or manually created pages, suitable for small sites with few articles. However, it’s labor-intensive to maintain, prone to errors (e.g., outdated metadata), and lacks flexibility for new content. Static rendering can harm SEO if pages aren’t updated regularly, as search engines may miss new content.
        </p>
        <p className="text-md mb-4">
          Choose dynamic rendering for scalability and SEO benefits, especially for sites with growing content. Static rendering may suffice for tiny sites with stable content but quickly becomes impractical.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">Generic Markdown Example</h2>
        <p className="text-md mb-4">
          Below is an example of a generic MD file stored in the <code>articles/</code> directory, demonstrating frontmatter and content structure.
        </p>
        <CodeMirror
          value={`---
title: Building a Web App with Next.js
description: Learn how to create a modern web application using Next.js, Tailwind CSS, and Markdown for content.
date: 2025-05-01
tags: [Next.js, Tailwind CSS, Markdown, Web Development]
---

# Building a Web App with Next.js

Next.js is a powerful framework for building modern web applications. This guide walks you through setting up a Next.js project with Tailwind CSS for styling and Markdown for content management.

## Getting Started

Install Next.js and Tailwind CSS:

\`\`\`bash
npx create-next-app@latest my-app
cd my-app
npm install tailwindcss@latest postcss@latest autoprefixer@latest
npx tailwindcss init -p
\`\`\`

## Adding Markdown Support

Use \`gray-matter\` and \`remark\` to process Markdown files:

\`\`\`bash
npm install gray-matter remark remark-gfm remark-rehype rehype-stringify rehype-highlight rehype-sanitize rehype-slug
\`\`\`

## Deploying Your App

Deploy to Vercel for seamless hosting:

\`\`\`bash
vercel
\`\`\`

This approach ensures your app is fast, SEO-friendly, and easy to maintain.`}
          extensions={[javascript()]}
          theme={githubDark}
          height="400px"
          className="mb-4"
        />
        <p className="text-md mb-4">
          This MD file includes frontmatter (title, description, date, tags) and content with code blocks. The dynamic rendering code processes it into an SEO-optimized page with a TOC, ideal for automated content workflows as discussed in our <Link href="/articles/advantages-seo-automation-seozast" className="text-emerald-500 hover:underline">SEO automation advantages guide</Link>.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">Conclusion</h2>
        <p className="text-md mb-4">
          Dynamic Markdown rendering in Next.js is a game-changer for SEO and content management. By storing content in MD files and using tools like <code>remark</code> and <code>rehype</code>, you can create scalable, search-engine-friendly pages with minimal effort. The provided code and folder structure make it easy to implement, while dynamic rendering outperforms static methods for growing sites. Pair this with a dynamic sitemap (<Link href="/articles/how-to-create-a-dynamic-sitemap-in-nextjs" className="text-emerald-500 hover:underline">dynamic sitemap guide</Link>) and automation tools like SEOZast (<Link href="/articles/seozast-seo-automation-easy-affordable" className="text-emerald-500 hover:underline">SEOZast automation guide</Link>) to maximize visibility and efficiency.
        </p>
      </section>

      <TrySeozastCTA />
      {/* FAQ Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "What is dynamic Markdown rendering?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Dynamic Markdown rendering allows you to generate pages in Next.js directly from Markdown files. It improves SEO and content scalability without hardcoding routes."
                }
              },
              {
                "@type": "Question",
                "name": "How do I render Markdown dynamically in Next.js?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "You can use libraries like remark and rehype to convert Markdown files into HTML and render them in dynamic routes such as /articles/[slug]/page.tsx."
                }
              },
              {
                "@type": "Question",
                "name": "Why is dynamic rendering better for SEO?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Dynamic rendering supports structured metadata, automatic TOC generation, and allows Google to index new content without code changes, making it ideal for SEO automation."
                }
              },
              {
                "@type": "Question",
                "name": "What is the difference between dynamic and static rendering?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Static rendering requires manual creation of pages, while dynamic rendering generates pages on the fly from Markdown files. Dynamic rendering is more scalable and SEO-friendly."
                }
              }
            ]
          })
        }}
      />
    </div>
  );
};

export default Page;